/* ============================================================
   Lab Effects — Shiptection eligibility gate
   assets/le-shiptection.js   (markup: snippets/le-shiptection.liquid)

   Shiptection mounts into any empty `.shiptection-widget` and, with its
   "auto check box" setting on, adds protection to the cart straight away.
   It has no order-value cap and no reliable country gate of its own, so this
   file decides whether the widget exists at all:

     eligible   = IP country in data-countries AND total <= data-max-cents
     'over'     = too big: widget removed. The "contact us" note shows ONLY
                  when the shopper was looking at the offer and pushed the
                  total over from INSIDE the cart (+/- in the drawer or
                  mobile cart). An add from anywhere else hides it all.
     'geo'      = outside the allowlist (or country unknown): block hidden

   Whenever the cart is NOT eligible and still holds the protection line, the
   line is removed and Shiptection's own "declined" cookie is set, so its
   script (which patches fetch and re-adds on cart changes) stands down.

   The result is written to cart attributes `_protection_geo` and
   `_protection_status` so every order records what the shopper was shown.

   Shiptection's own add/remove writes the cart with a bare
   POST /cart/update.js {updates} and repaints nothing (its "refresh" options
   are off, because a full reload closed the drawer). A PerformanceObserver
   below spots those writes and re-renders every cart surface the theme's way.
   See docs/shiptection.md.
   ============================================================ */
(function () {
  'use strict';
  if (window.LEShiptection) return;

  var GEO_KEY = 'le-shipt-geo';
  var DECLINED_COOKIE = 'shiptection-declined-insurance';
  var MOBILE = '(max-width: 767px)';
  var HANDLE = 'shiptection-shipping-protection';
  var SELF_URL = '/cart/update.js?le_shipt=1'; // our writes, so the watcher skips them

  var geoPromise = null;
  var running = false;
  // Where the change being evaluated came from: 'load', 'cart' (a +/- or
  // remove inside the drawer / mobile cart), 'external' (PDP add, quick add,
  // anything else), or 'internal' (our own / Shiptection's repaint).
  var trigger = 'load';
  var prevStatus = null;
  var noteOn = false;
  var lastHadProtection = false;
  var queued = false;

  function store(kind) {
    try { return window[kind]; } catch (e) { return null; }
  }
  function sget(k) { try { var s = store('sessionStorage'); return s && s.getItem(k); } catch (e) { return null; } }
  function sset(k, v) { try { var s = store('sessionStorage'); if (s) s.setItem(k, v); } catch (e) {} }

  // Shopify's own IP geolocation. Unknown => null => treated as ineligible.
  function getCountry() {
    var cached = sget(GEO_KEY);
    if (cached) return Promise.resolve(cached === '??' ? null : cached);
    if (!geoPromise) {
      geoPromise = fetch('/browsing_context_suggestions.json', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (d) {
          var c = d && d.detected_values && d.detected_values.country && d.detected_values.country.handle;
          c = c ? String(c).toUpperCase() : null;
          sset(GEO_KEY, c || '??');
          return c;
        })
        .catch(function () { return null; });
    }
    return geoPromise;
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.json(); });
  }

  // The wrapper that should host the widget: mobile cart below 768px,
  // otherwise the first desktop one (the drawer). Only ever ONE mount, since
  // each Shiptection mount runs its own auto-add.
  function wrappers() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-le-shipt]'));
  }
  function activeWrapper(list) {
    var mobile = window.matchMedia && window.matchMedia(MOBILE).matches;
    for (var i = 0; i < list.length; i++) {
      var inMobile = !!list[i].closest('.le-mobile-cart-section, [data-mcart]');
      if (inMobile === mobile) return list[i];
    }
    return null;
  }

  // What Shiptection WOULD charge for this cart, from the same /quote endpoint
  // its widget calls. Used when protection is not in the cart yet, so the cap
  // can be tested on goods + fee. Cached per cart contents; null on failure.
  var quoteCache = {};
  function getFeeCents(cart) {
    var items = (cart.items || []).filter(function (it) { return it.handle !== HANDLE; })
      .map(function (it) { return { product_id: it.product_id, price: it.price, quantity: it.quantity }; });
    if (!items.length) return Promise.resolve(0);
    var sig = JSON.stringify(items);
    if (quoteCache[sig] !== undefined) return Promise.resolve(quoteCache[sig]);
    var shop = (window.Shopify && Shopify.shop) || '';
    var currency = (cart.currency) || 'USD';
    return fetch('https://api.shiptection.com/quote', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items, shop: shop, currency: currency })
    })
      .then(function (r) { return r.json(); })
      .then(function (q) {
        var cents = q && typeof q.price === 'number' ? Math.round(q.price * 100) : null;
        quoteCache[sig] = cents;
        return cents;
      })
      .catch(function () { return null; });
  }

  // The $5,000 cap is tested on goods + protection fee (Dustin, 2026-09-22,
  // until Shiptection confirms whether the fee counts). feeCents is the quote
  // when protection is not in the cart; the real line is used when it is.
  function evaluate(cart, country, cfg, feeCents) {
    var pid = cfg.productId;
    var protection = 0;
    var protectedLines = [];
    (cart.items || []).forEach(function (it) {
      if (it.product_id === pid || it.handle === HANDLE) {
        protection += it.final_line_price;
        protectedLines.push(it);
      }
    });
    var goods = cart.total_price - protection;
    var fee = protectedLines.length ? protection : (feeCents || 0);
    // Cart is in the presentment currency; the cap is in the shop currency (USD).
    var rate = parseFloat(window.Shopify && Shopify.currency && Shopify.currency.rate) || 1;
    var withFeeShop = (goods + fee) / rate;

    var status;
    if (!country || cfg.countries.indexOf(country) === -1) status = 'hidden_geo';
    else if (goods <= 0) status = 'hidden_empty';
    else if (withFeeShop > cfg.maxCents) status = 'hidden_over_limit';
    else status = 'offered';

    return { status: status, lines: protectedLines, country: country || 'unknown' };
  }

  function setDeclinedCookie() {
    // Same cookie + lifetime (2h) Shiptection's own script sets on decline.
    var exp = new Date(Date.now() + 2 * 60 * 60 * 1000).toUTCString();
    document.cookie = DECLINED_COOKIE + '=true; expires=' + exp + '; path=/';
  }

  function cartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      var id = el.dataset && el.dataset.sectionId;
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    if (document.querySelector('[data-mcart]')) ids.push('le-mobile-cart');
    return ids;
  }

  // Take protection out of the cart and refresh every cart surface.
  function removeProtection(lines) {
    setDeclinedCookie();
    var updates = {};
    lines.forEach(function (l) { updates[l.key] = 0; });
    var ids = cartSectionIds();
    var body = { updates: updates };
    // Render against THIS page: on /cart the header omits the drawer entirely.
    if (ids.length) { body.sections = ids.join(','); body.sections_url = window.location.pathname; }
    return fetch(SELF_URL, {
      method: 'POST',
      headers: selfHeaders(),
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var sections = cart.sections || {};
        delete cart.sections;
        announce(cart, sections);
        return cart;
      });
  }

  function selfHeaders() {
    return { 'Content-Type': 'application/json', Accept: 'application/json' };
  }

  function goodsCount(cart) {
    var n = cart.item_count || 0;
    (cart.items || []).forEach(function (it) { if (it.handle === HANDLE) n -= it.quantity; });
    return n;
  }

  // Repaint every cart surface from fresh section HTML WITHOUT a cart:update
  // event. That event is also Horizon's CartAddEvent: with auto-open on it pops
  // the drawer open, and the drawer's own listener full-morphs its section,
  // which strips the <dialog>'s open state and slams an open drawer shut.
  // Instead: the drawer is morphed in 'hydration' mode (what Horizon itself
  // uses for its own drawer updates), then each cart listener is fed directly.
  function announce(cart, sections) {
    var count = goodsCount(cart);
    if (sections['le-mobile-cart'] && window.LEMobileCart) window.LEMobileCart.swap(sections['le-mobile-cart']);

    var ids = Object.keys(sections).filter(function (k) { return k !== 'le-mobile-cart' && sections[k]; });
    // window.LE_morphSection is Horizon's morphSection, exposed by a module
    // script in layout/theme.liquid (an import() here would be minified away).
    if (ids.length && typeof window.LE_morphSection === 'function') {
      ids.forEach(function (id) {
        var el = document.getElementById('shopify-section-' + id);
        var mode = el && el.querySelector('cart-drawer-component') ? 'hydration' : 'full';
        try { window.LE_morphSection(id, sections[id], { mode: mode }); } catch (e) {}
      });
    }

    document.querySelectorAll('cart-icon').forEach(function (icon) {
      if (typeof icon.renderCartBubble === 'function') icon.renderCartBubble(count, false, false);
    });
    document.querySelectorAll('[data-le-cart-count]').forEach(function (b) {
      b.textContent = count;
      b.hidden = count <= 0;
    });
    var total = cart.total_price;
    (cart.items || []).forEach(function (it) { if (it.handle === HANDLE) total -= it.final_line_price; });
    if (window.LEFreeShipping) window.LEFreeShipping.update(total);
    schedule(1500);
  }

  // After Shiptection writes the cart, fetch the cart + section HTML and repaint.
  // Shiptection re-prices by REMOVING its line and adding a new one, which
  // renumbers the cart. Horizon's +/- buttons address lines by number, so a
  // click before the repaint lands would change the wrong line (the
  // protection). Freeze the item rows (Horizon's own disabled class) until the
  // last repaint is in.
  var unlockTimer = null;
  function lockItems() {
    document.querySelectorAll('cart-items-component').forEach(function (el) { el.classList.add('cart-items-disabled'); });
    document.querySelectorAll('.le-mcart__items').forEach(function (el) { el.style.pointerEvents = 'none'; });
    clearTimeout(unlockTimer);
    unlockTimer = setTimeout(function () {
      document.querySelectorAll('cart-items-component').forEach(function (el) { el.classList.remove('cart-items-disabled'); });
      document.querySelectorAll('.le-mcart__items').forEach(function (el) { el.style.pointerEvents = ''; });
    }, 2200);
  }

  function syncAfterShiptection() {
    var ids = cartSectionIds();
    var sectionsReq = ids.length
      ? fetch(window.location.pathname + '?sections=' + encodeURIComponent(ids.join(',')) + '&_=' + Date.now(), { cache: 'no-store' }).then(function (r) { return r.json(); })
      : Promise.resolve({});
    Promise.all([getCart(), sectionsReq])
      .then(function (res) { announce(res[0], res[1] || {}); })
      .catch(function () {});
  }

  // Watch for Shiptection's cart writes. Wrapping fetch does not work: its
  // bundle saves the native fetch at load and later assigns its own wrapper
  // over window.fetch, bypassing anything installed in between. The browser's
  // resource timeline sees every request regardless. Shiptection writes with
  // a bare /cart/update.js; ours carry ?le_shipt=1, Horizon uses change.js.
  if (window.PerformanceObserver) {
    try {
      new PerformanceObserver(function (list) {
        var hit = list.getEntries().some(function (en) {
          return /\/cart\/update(\.js)?(\?|$)/.test(en.name) && en.name.indexOf('le_shipt=1') === -1 &&
            (en.initiatorType === 'fetch' || en.initiatorType === 'xmlhttprequest');
        });
        // A render fired the instant the write returns can still see the old
        // cart, so repaint shortly after, then once more to be sure.
        if (hit) { lockItems(); setTimeout(syncAfterShiptection, 350); setTimeout(syncAfterShiptection, 1400); }
      }).observe({ type: 'resource' });
    } catch (e) {}
  }

  // Only write when something changed; every cart write wakes Shiptection's
  // fetch interceptor, so an unconditional write would churn.
  function recordAttributes(cart, result) {
    var a = cart.attributes || {};
    if (a._protection_geo === result.country && a._protection_status === result.status) return Promise.resolve();
    return fetch(SELF_URL, {
      method: 'POST',
      headers: selfHeaders(),
      body: JSON.stringify({ attributes: { _protection_geo: result.country, _protection_status: result.status } })
    }).catch(function () {});
  }

  function paint(list, active, result, dark) {
    list.forEach(function (w) {
      guardClicks(w);
      var slot = w.querySelector('[data-le-shipt-slot]');
      var over = w.querySelector('[data-le-shipt-over]');
      var widget = slot && slot.querySelector('.shiptection-widget');

      if (w !== active) {
        if (widget) widget.remove();
        w.hidden = true;
        return;
      }

      if (result.status === 'offered') {
        if (!widget && slot) {
          widget = document.createElement('div');
          widget.className = 'shiptection-widget';
          widget.setAttribute('max-width', 'true');
          if (dark) widget.setAttribute('dark-ui', '');
          slot.appendChild(widget);
        }
        if (over) over.hidden = true;
        w.hidden = false;
      } else {
        if (widget) widget.remove();
        var showNote = result.status === 'hidden_over_limit' && noteOn;
        if (over) over.hidden = !showNote;
        w.hidden = !showNote;
      }
    });
    if (window.LEMobileCart && window.LEMobileCart.fitFoot) window.LEMobileCart.fitFoot();
    adoptModals();
  }

  function run() {
    if (running) { queued = true; return; }
    var list = wrappers();
    if (!list.length) return;
    var first = list[0];
    var cfg = {
      productId: parseInt(first.dataset.productId, 10),
      maxCents: parseInt(first.dataset.maxCents, 10) || 500000,
      countries: String(first.dataset.countries || 'US').toUpperCase().split(/\s*,\s*/)
    };
    running = true;
    // Claim the trigger now, so a click that lands mid-run is kept for the
    // queued re-run instead of being overwritten.
    var from = trigger;
    trigger = 'internal';

    Promise.all([getCart(), getCountry()])
      .then(function (res) {
        var cart = res[0];
        var hasLine = (cart.items || []).some(function (it) { return it.handle === HANDLE; });
        return (hasLine ? Promise.resolve(0) : getFeeCents(cart)).then(function (fee) { return [cart, res[1], fee]; });
      })
      .then(function (res) {
        var cart = res[0];
        var result = evaluate(cart, res[1], cfg, res[2]);
        lastHadProtection = result.lines.length > 0;

        if (result.status !== 'hidden_over_limit') noteOn = false;
        else if (from === 'cart' && (prevStatus === 'offered' || noteOn)) noteOn = true;
        else if (from !== 'internal') noteOn = false;
        prevStatus = result.status;

        var active = activeWrapper(wrappers());
        paint(wrappers(), active, result, active && active.hasAttribute('data-dark'));

        var chain = Promise.resolve(cart);
        if (result.status !== 'offered' && result.lines.length) {
          chain = removeProtection(result.lines);
        }
        return chain.then(function (c) { return recordAttributes(c || cart, result); });
      })
      .catch(function () {})
      .then(function () {
        running = false;
        if (queued) { queued = false; run(); }
      });
  }

  var timer = null;
  function schedule(delay, from) {
    if (from) trigger = from;
    clearTimeout(timer);
    timer = setTimeout(run, delay || 150);
  }

  window.LEShiptection = { refresh: run };

  // LAST ITEM REMOVED -> remove protection in the SAME request.
  // Shiptection's own code (its "Refresh 2" path) reacts to a cart holding
  // ONLY its protection line by deleting it and calling location.reload(),
  // whatever the refresh settings say. That reload is the drawer "shutting
  // and reopening" when the last product is trashed. So when the click would
  // remove the last real product, we take it over (capture phase, before
  // Horizon / le-mobile-cart see it) and zero that line + the protection in
  // one /cart/update.js. Shiptection then only ever sees an empty cart.
  function lastGoodsRemoval(e) {
    var t = e.target;
    if (!t || !t.closest) return null;
    var btn = t.closest('cart-items-component [on\\:click*="onLineItemRemove"]');
    if (btn) {
      var row = btn.closest('tr');
      var comp = btn.closest('cart-items-component');
      var goods = comp ? comp.querySelectorAll('tbody tr[data-key]:not([data-le-shipt-line])').length : 0;
      var hasProt = comp && comp.querySelector('tbody tr[data-le-shipt-line]');
      return row && goods === 1 && hasProt ? row.getAttribute('data-key') : null;
    }
    btn = t.closest('.le-mcitem [data-qty-remove], .le-mcitem [data-qty-down]');
    if (btn) {
      var item = btn.closest('.le-mcitem');
      if (btn.hasAttribute('data-qty-down')) {
        var q = item && item.querySelector('[data-qty]');
        if (q && (parseInt(q.textContent, 10) || 1) > 1) return null;
      }
      var count = document.querySelectorAll('.le-mcitem').length;
      return item && count === 1 && lastHadProtection ? item.getAttribute('data-key') : null;
    }
    return null;
  }

  // On WINDOW, capture phase: Horizon's declarative on:click handlers are a
  // capture listener on document (component.js), and window runs first.
  window.addEventListener('click', function (e) {
    var key = lastGoodsRemoval(e);
    if (!key) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    lockItems();
    getCart().then(function (cart) {
      var updates = {};
      updates[key] = 0;
      var others = (cart.items || []).filter(function (it) { return it.key !== key && it.handle !== HANDLE; });
      if (!others.length) {
        (cart.items || []).forEach(function (it) { if (it.handle === HANDLE) updates[it.key] = 0; });
      }
      var ids = cartSectionIds();
      var body = { updates: updates };
      if (ids.length) { body.sections = ids.join(','); body.sections_url = window.location.pathname; }
      return fetch(SELF_URL, { method: 'POST', headers: selfHeaders(), body: JSON.stringify(body) });
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var sections = cart.sections || {};
        delete cart.sections;
        announce(cart, sections);
      })
      .catch(function () {});
  }, true);

  // Shiptection renders its "Are you sure?" removal modal as a direct child
  // of <body> when the widget mounts. The cart drawer is a modal <dialog> in
  // the top layer, so that popup sits BEHIND it: the click lands on the drawer
  // backdrop, Horizon closes the drawer, and only then can the popup be
  // clicked. Move it into the dialog hosting the live widget (React keeps
  // working on a moved node). Not on the mobile cart page: no dialog there.
  function adoptModals() {
    var live = document.querySelector('[data-le-shipt]:not([hidden]) .shiptection-widget');
    var dlg = live && live.closest('dialog');
    if (!dlg) return;
    Array.prototype.forEach.call(document.body.children, function (n) {
      if (/shiptection-modal/.test(n.className || '')) { dlg.appendChild(n); guardClicks(n); }
    });
  }

  // Horizon's drawer closes on any click whose target is not inside it
  // (utilities.js isClickedOutside -> !dialog.contains(target)). Shiptection
  // re-renders on click: REMOVE turns into "Loading...", the confirm modal
  // unmounts. By the time the click bubbles to the <dialog> its target is
  // DETACHED, so contains() is false and the drawer shuts. React has already
  // handled the click at its own root below this node, so stopping it here
  // costs Shiptection nothing.
  function guardClicks(el) {
    if (!el || el.__leShiptGuard) return;
    el.__leShiptGuard = true;
    el.addEventListener('click', function (e) { e.stopPropagation(); });
  }
  if (window.MutationObserver) {
    new MutationObserver(adoptModals).observe(document.body, { childList: true });
  }

  document.addEventListener('cart:update', function (e) {
    var d = (e && e.detail) || {};
    var src = (d.data && d.data.source) || '';
    var from = (src === 'cart-items-component' || d.sourceId === 'le-mobile-cart') ? 'cart' : 'external';
    schedule(150, from);
  });
  if (window.matchMedia) {
    var mq = window.matchMedia(MOBILE);
    if (mq.addEventListener) mq.addEventListener('change', function () { schedule(); });
  }
  if (document.readyState !== 'loading') schedule(0, 'load');
  else document.addEventListener('DOMContentLoaded', function () { schedule(0, 'load'); });
})();
